import { Injectable, inject, LOCALE_ID } from '@angular/core';
import { Amplify } from 'aws-amplify';
import { signInWithRedirect, signOut, getCurrentUser, fetchAuthSession, AuthError } from 'aws-amplify/auth';
import { Router } from '@angular/router';
import { amplifyConfig } from '../../utils/amplify/amplify';
import { BackendService } from '../../api/bff/backend.service';
import { firstValueFrom } from 'rxjs';
import { AuthServiceInterface } from './auth.service.interface';
import { User } from '@shared/interfaces/user';
import { eulaVersion } from '@shared/utils/eulaVersion';
import { ModalService } from '../modal/modal.service';
import { ProfileViewComponent } from '../../layout/components/profile-view/profile-view.component';
import { AnalyticsService } from '../analytics/analytics.service';
import { EulaViewComponent } from '../../layout/components/eula-view/eula-view.component';
import { changeUrlToLanguage } from '../language/languages';

interface Tokens {
  accessToken: string | null;
  idToken: string | null;
}

@Injectable({
  providedIn: 'root',
})
export class AuthService implements AuthServiceInterface {
  private router = inject(Router);
  private locale = inject(LOCALE_ID);
  private backend = inject(BackendService);
  private modalService = inject(ModalService);
  private analyticsService = inject(AnalyticsService);
  public user: Promise<User | null>;
  private cachedTokens: Tokens | null = null;

  constructor() {
    Amplify.configure(amplifyConfig);
    this.user = this.setupUser();
  }

  autoSignIn(idp: string | null) {
    const options = idp ? { provider: { custom: idp } } : undefined;
    return signInWithRedirect(options);
  }

  signUp() {
    const redirectTo = window.location.origin;
    const signupUrl = `https://${amplifyConfig.Auth.Cognito.loginWith.oauth.domain}/signup?client_id=${amplifyConfig.Auth.Cognito.userPoolClientId}&response_type=code&scope=openid+email&redirect_uri=${redirectTo}`;
    window.location.assign(signupUrl);
  }

  signIn() {
    return signInWithRedirect().catch((error) => {
      if (error instanceof AuthError) {
        if (error.name === 'UserAlreadyAuthenticatedException') {
          // User is already signed in, so we sign them out first
          return this.signOut();
        }
      }
      return Promise.reject(error);
    });
  }

  signOut() {
    this.cachedTokens = null;
    this.router.navigate(['/']);
    return signOut({ global: true });
  }

  setUser(user: User) {
    this.user = Promise.resolve(user);
  }

  getTokens() {
    if (!this.cachedTokens) {
      return fetchAuthSession().then((session) => {
        this.cachedTokens = {
          accessToken: session?.tokens?.accessToken.toString() || null,
          idToken: session?.tokens?.idToken?.toString() || null,
        };
        return this.cachedTokens;
      });
    }
    return Promise.resolve(this.cachedTokens);
  }

  private ensureEula(user: User) {
    if (!user.eulaVersion || user.eulaVersion < eulaVersion) {
      this.modalService.openModal(EulaViewComponent, {
        data: { signupFlow: false },
        cancellable: false,
      });
    }
  }

  private ensureProfile(user: User) {
    if (!user.role || !user.yearsInIndustry || !user.country) {
      this.modalService.openModal(ProfileViewComponent, { cancellable: false });
    }
  }

  private setupUser(): Promise<User | null> {
    return new Promise<User | null>((resolve, _) => {
      getCurrentUser()
        .then(() => firstValueFrom(this.backend.getUser()))
        .then((user) => {
          if (user.language === this.locale) {
            return user;
          }
          changeUrlToLanguage(user.language);
          return user;
        })
        .then((user) => {
          this.ensureProfile(user);
          this.ensureEula(user);
          this.analyticsService.setUserData(user);
          resolve(user);
        })
        .catch((_err) => {
          // Not logged in, getCurrentUser() will throw an error
          resolve(null);
        });
    });
  }
}
